import { useCallback, useMemo, useState } from "react";
import type { ChangeEvent } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Group,
  Image,
  Loader,
  Paper,
  Stack,
  Text,
} from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import type {
  LegacyReportDraftData,
  LegacyReportManifest,
  LegacyReportReviewAsset,
} from "../../../types/legacyReportReview";
import { useLegacyReportBlob } from "../../../hooks/useLegacyReportBlob";
import LegacyReportAssetPlacement from "./LegacyReportAssetPlacement";

interface Props {
  asset: LegacyReportReviewAsset;
  evidence?: LegacyReportManifest["assets"][number];
  form: UseFormReturnType<LegacyReportDraftData>;
  readOnly?: boolean;
}

export default function LegacyReportAssetReview({
  asset,
  evidence,
  form,
  readOnly = false,
}: Props) {
  const { url, failed, load } = useLegacyReportBlob(asset.contentUrl);
  const [decoded, setDecoded] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const reviewed = useMemo(
    () => form.values.reviewedAssetIds.includes(asset.id),
    [form.values.reviewedAssetIds, asset.id]
  );
  const handleLoad = useCallback(() => {
    setDecoded(true);
    setImageFailed(false);
  }, []);
  const handleError = useCallback(() => {
    setDecoded(false);
    setImageFailed(true);
  }, []);
  const handleRetry = useCallback(() => {
    setDecoded(false);
    setImageFailed(false);
    void load();
  }, [load]);
  const handleReview = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const ids = form
        .getValues()
        .reviewedAssetIds.filter((id) => id !== asset.id);
      form.setFieldValue(
        "reviewedAssetIds",
        event.currentTarget.checked ? [...ids, asset.id] : ids
      );
    },
    [form, asset.id]
  );
  return (
    <Paper withBorder p="md">
      <Stack gap="sm">
        <Text fw={600}>
          Source page {asset.sourcePage || evidence?.sourcePage || "—"} ·{" "}
          {evidence?.role === "organMapCandidate"
            ? "Organ map and legend"
            : evidence?.role === "signatureCandidate"
            ? "Signature region"
            : "Preserved page evidence"}
        </Text>
        {evidence?.region && (
          <Text size="xs" c="dimmed">
            Source bounds: x {evidence.region.x}, y {evidence.region.y}, width{" "}
            {evidence.region.width}, height {evidence.region.height}
          </Text>
        )}
        {failed || imageFailed ? (
          <Alert color="red" title="Source graphic unavailable">
            <Button onClick={handleRetry} variant="light">
              Retry graphic
            </Button>
          </Alert>
        ) : url ? (
          <Image
            src={url}
            alt={`Historical source graphic on page ${
              asset.sourcePage || evidence?.sourcePage || "unknown"
            }`}
            fit="contain"
            h={260}
            onLoad={handleLoad}
            onError={handleError}
          />
        ) : (
          <Loader aria-label="Loading source graphic" />
        )}
        <Group justify="space-between">
          <Checkbox
            label="I checked this graphic, its labels, and its boundaries against the original"
            checked={reviewed}
            onChange={handleReview}
            disabled={readOnly || !decoded || failed || imageFailed}
          />
          {url && (
            <Button
              component="a"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              variant="subtle"
            >
              Open full graphic
            </Button>
          )}
        </Group>
        <LegacyReportAssetPlacement
          asset={asset}
          form={form}
          disabled={readOnly || !decoded || failed || imageFailed}
        />
      </Stack>
    </Paper>
  );
}
